import React, { useState, useRef } from 'react';
import { useClients } from '../contexts/ClientContext';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Image as ImageIcon,
  CloudUpload as UploadIcon,
  Send as SendIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
  ClientSelector,
  FormDialog,
  PageHeader,
  StatusChip,
} from '../components/primitives';

const UploadZone = styled(Box)(({ theme, isDragging }) => ({
  border: `2px dashed ${isDragging ? '#58a6ff' : 'rgba(48, 54, 61, 0.8)'}`,
  borderRadius: 12,
  padding: 48,
  textAlign: 'center',
  bgcolor: isDragging ? 'rgba(88, 166, 255, 0.08)' : 'rgba(13, 17, 23, 0.4)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: 'rgba(88, 166, 255, 0.6)',
    bgcolor: 'rgba(88, 166, 255, 0.06)',
  },
}));

const PLATFORMS = [
  { id: '500px', label: '500px', url: 'https://500px.com' },
  { id: 'flickr', label: 'Flickr', url: 'https://flickr.com' },
  { id: 'deviantart', label: 'DeviantArt', url: 'https://deviantart.com' },
];

// Manual login (OAuth/SSO) — the browser window is the credential, nothing to store.
const MANUAL_LOGIN_PLATFORMS = ['500px', 'flickr', 'deviantart'];

export default function ImageDirectories() {
  const { clients } = useClients();
  const [selectedClient, setSelectedClient] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [keywords, setKeywords] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState({ '500px': false, flickr: false, deviantart: false });
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [results, setResults] = useState([]);
  const fileInputRef = useRef(null);
  const [connectedPlatforms, setConnectedPlatforms] = useState({});
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [connectPlatform, setConnectPlatform] = useState('');
  const [connectUsername, setConnectUsername] = useState('');
  const [connectPassword, setConnectPassword] = useState('');
  const [connectLoading, setConnectLoading] = useState(false);
  const HISTORY_KEY = (clientId) => `imageDirectoriesHistory_${clientId || 'none'}`;

  const [history, setHistory] = useState([]);

  const loadLocalHistory = (clientId) => {
    try {
      const s = localStorage.getItem(HISTORY_KEY(clientId));
      return s ? JSON.parse(s) : [];
    } catch {
      return [];
    }
  };

  const saveHistory = (clientId, entries) => {
    const trimmed = entries.slice(0, 50);
    setHistory(trimmed);
    try {
      localStorage.setItem(HISTORY_KEY(clientId), JSON.stringify(trimmed));
    } catch (e) {}
  };

  const fetchHistory = async (clientId) => {
    if (!clientId) {
      setHistory([]);
      return;
    }
    try {
      const res = await fetch(
        `${getApiBase()}/image-directories/history?client_id=${encodeURIComponent(clientId)}`,
        { headers: authHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        const server = Array.isArray(data.history) ? data.history : [];
        // Server is source of truth (manual + auto-post). Keep any local-only
        // leftovers that predate persistence, merged by id.
        const local = loadLocalHistory(clientId);
        const byId = new Map();
        [...server, ...local].forEach((e) => {
          if (e && e.id != null && !byId.has(e.id)) byId.set(e.id, e);
        });
        const merged = Array.from(byId.values()).sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        setHistory(merged.slice(0, 50));
        return;
      }
    } catch (e) {
      console.error(e);
    }
    setHistory(loadLocalHistory(clientId));
  };

  const getApiBase = () => {
    const base = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    return base.replace(/\/api\/v1\/?$/, '') + '/api/v1';
  };

  // Credentials are per client (W8.BUG-2) — refetch whenever the workspace changes.
  const fetchCredentials = async (clientId) => {
    if (!clientId) {
      setConnectedPlatforms({});
      return;
    }
    try {
      const res = await fetch(
        `${getApiBase()}/image-directories/credentials?client_id=${encodeURIComponent(clientId)}`,
        { headers: authHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setConnectedPlatforms(data.credentials || {});
      } else {
        setConnectedPlatforms({});
      }
    } catch (e) {
      console.error(e);
      setConnectedPlatforms({});
    }
  };

  React.useEffect(() => {
    fetchHistory(selectedClient);
    fetchCredentials(selectedClient);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when the workspace changes
  }, [selectedClient]);

  React.useEffect(() => {
    if (clients.length > 0 && !selectedClient) setSelectedClient(clients[0].id);
  }, [clients, selectedClient]);

  const handleFileSelect = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setError('Please select an image file (jpg, png, gif, webp).');
      return;
    }
    setImageFile(file);
    setError('');
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handlePlatformToggle = (id) => {
    setSelectedPlatforms((p) => ({ ...p, [id]: !p[id] }));
  };

  const authHeaders = () => ({
    ...(localStorage.getItem('access_token') && {
      Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    }),
  });

  const handlePost = async () => {
    if (!selectedClient) {
      setError('Select a client first — profiles and logins are per client.');
      return;
    }
    if (!imageFile) {
      setError('Select an image first.');
      return;
    }
    const platforms = Object.entries(selectedPlatforms)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (platforms.length === 0) {
      setError('Select at least one platform.');
      return;
    }
    setError('');
    setSuccess('');
    setLoading(true);
    setResults([]);
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('title', title);
      formData.append('description', description);
      formData.append('keywords', keywords || '');
      formData.append('platforms', JSON.stringify(platforms));
      formData.append('client_id', selectedClient);

      const res = await fetch(
        `${getApiBase()}/image-directories/post`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: formData,
        }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSuccess(data.message || 'Posted to selected platforms.');
        setResults(data.results || []);
        await fetchHistory(selectedClient);
      } else {
        setError(data.detail || data.error || 'Post failed.');
        // Optimistic local row when the request failed before a server history write
        saveHistory(selectedClient, [
          {
            id: Date.now(),
            date: new Date().toISOString(),
            title: title || imageFile?.name || 'Untitled',
            platforms: [],
            allSuccess: false,
            error: data.detail || data.error || 'Post failed.',
          },
          ...loadLocalHistory(selectedClient),
        ]);
      }
    } catch (e) {
      setError(e.message || 'Post failed.');
      saveHistory(selectedClient, [
        {
          id: Date.now(),
          date: new Date().toISOString(),
          title: title || imageFile?.name || 'Untitled',
          platforms: [],
          allSuccess: false,
          error: e.message || 'Post failed.',
        },
        ...loadLocalHistory(selectedClient),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectClick = (platformId) => {
    setConnectPlatform(platformId);
    setConnectUsername(connectedPlatforms[platformId]?.username || '');
    setConnectPassword('');
    setConnectDialogOpen(true);
  };

  const handleConnectSubmit = async () => {
    if (!connectPlatform) return;
    if (!selectedClient) {
      setError('Select a client first — each client connects its own account.');
      return;
    }
    // 500px, Flickr, DeviantArt: manual login - no credentials needed for connect
    const needsCredentials = !MANUAL_LOGIN_PLATFORMS.includes(connectPlatform);
    if (needsCredentials && (!connectUsername || !connectPassword)) {
      setError('Enter username and password.');
      return;
    }
    setConnectLoading(true);
    setError('');
    const apiBase = getApiBase();
    try {
      if (needsCredentials) {
        let res = await fetch(`${apiBase}/image-directories/credentials`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({
            client_id: Number(selectedClient),
            platform: connectPlatform,
            username: connectUsername,
            password: connectPassword,
          }),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || 'Failed to save credentials');
        }
      }
      // The browser window opens on the home Mac. This request stays open until the
      // session is verified there, so it can take a few minutes.
      const res = await fetch(`${apiBase}/image-directories/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ client_id: Number(selectedClient), platform: connectPlatform }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.verified) {
        setSuccess(data.message || 'Connected. Session verified.');
        setConnectDialogOpen(false);
        fetchCredentials(selectedClient);
      } else {
        fetchCredentials(selectedClient);
        throw new Error(data.detail || data.reason || data.error || 'Connect failed — session not verified.');
      }
    } catch (e) {
      setError(e.message || 'Connect failed');
    } finally {
      setConnectLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <PageHeader
        title="Image Directories"
        subtitle="Post images to 500px, Flickr and DeviantArt. Persistent browser profiles—connect once per client, per platform."
        icon={<ImageIcon sx={{ fontSize: 28, color: '#58a6ff' }} />}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(248,81,73,0.1)', color: '#f85149' }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, bgcolor: 'rgba(56,139,253,0.1)', color: '#58a6ff' }}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            1. Select client
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Logins and browser profiles are per client. Connecting 500px for one client does not connect it for another.
          </Typography>
          <ClientSelector
            clients={clients}
            value={selectedClient || ''}
            onChange={(client) => setSelectedClient(client?.id || '')}
            label="Client"
            fullWidth
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            2. Image & metadata
          </Typography>
          <UploadZone
            isDragging={isDragging}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect(e.target.files?.[0])}
            />
            {imagePreview ? (
              <Box>
                <Box component="img" src={imagePreview} alt="Preview" sx={{ maxHeight: 200, maxWidth: '100%', borderRadius: 2, mb: 2 }} />
                <Typography variant="body2" sx={{ color: '#8b949e' }}>{imageFile?.name}</Typography>
                <Button size="small" sx={{ mt: 1, color: '#58a6ff' }} onClick={(e) => { e.stopPropagation(); setImageFile(null); setImagePreview(null); }}>Remove</Button>
              </Box>
            ) : (
              <>
                <UploadIcon sx={{ fontSize: 48, color: '#58a6ff', mb: 1 }} />
                <Typography variant="body1" sx={{ color: '#c9d1d9' }}>Drop an image here or click to upload</Typography>
              </>
            )}
          </UploadZone>
          <Box style={{ display: 'flex', gap: 16, marginTop: 16, width: '100%', flexWrap: 'wrap' }}>
            <Box style={{ flex: '0 0 200px', minWidth: 200 }}>
              <TextField fullWidth label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
            </Box>
            <Box style={{ flex: 1, minWidth: 200 }}>
              <TextField fullWidth label="Description" multiline rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
            </Box>
            <Box style={{ flex: '1 1 100%', minWidth: 200 }}>
              <TextField
                fullWidth
                label="Keywords / tags"
                placeholder="Comma-separated (e.g. tattoo, portrait, black and white)"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                helperText="Comma-separated. Used as Flickr tags; 500px's current uploader has no keywords field and ignores them."
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            3. Platforms
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect once per platform for this client. A platform counts as connected only after
            its saved login has been verified—not just because a login window was opened.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            {PLATFORMS.map((p) => {
              const state = connectedPlatforms[p.id];
              const verified = !!state?.connected;
              // username falls back to the platform id when the probe could not read an
              // account name — show just the label rather than "500px (500px)".
              const named = verified && state.username && state.username !== p.id;
              const label = named
                ? `${p.label} (${state.username})`
                : verified
                ? p.label
                : state
                ? `${p.label} — not verified`
                : p.label;
              return (
                <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={label}
                    variant="outlined"
                    title={
                      verified
                        ? `Session verified ${new Date(state.session_verified_at).toLocaleString()}`
                        : state?.last_verify_error || 'No verified session saved for this client.'
                    }
                    sx={{
                      color: verified ? '#3fb950' : state ? '#d29922' : '#8b949e',
                      borderColor: selectedPlatforms[p.id] ? '#58a6ff' : 'rgba(48,54,61,0.5)',
                    }}
                  />
                  <Button
                    size="small"
                    sx={{ color: '#58a6ff' }}
                    disabled={!selectedClient}
                    onClick={() => handleConnectClick(p.id)}
                  >
                    {verified ? 'Reconnect' : 'Connect'}
                  </Button>
                </Box>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {PLATFORMS.map((p) => (
              <FormControlLabel
                key={p.id}
                control={<Checkbox checked={!!selectedPlatforms[p.id]} onChange={() => handlePlatformToggle(p.id)} sx={{ color: '#58a6ff' }} />}
                label={p.label}
                sx={{ color: '#c9d1d9' }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          onClick={handlePost}
          disabled={loading}
          sx={{ bgcolor: '#58a6ff', '&:hover': { bgcolor: '#1f6feb' } }}
        >
          {loading ? 'Posting…' : 'Post to selected platforms'}
        </Button>
      </Box>

      <FormDialog
        open={connectDialogOpen}
        maxWidth="sm"
        title={`Connect ${PLATFORMS.find((p) => p.id === connectPlatform)?.label || connectPlatform}`}
        submitLabel={connectLoading ? 'Waiting for login…' : 'Open login window'}
        loading={connectLoading}
        onSubmit={handleConnectSubmit}
        onClose={() => setConnectDialogOpen(false)}
      >
        {MANUAL_LOGIN_PLATFORMS.includes(connectPlatform) ? (
          <Typography variant="body2" color="text.secondary">
            A browser window opens on the scrape Mac. Log in there; the session is saved to this
            client's profile and verified before this dialog reports success. Nothing is typed here.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Save your login. We will log in once and keep the session in a persistent profile so future posts work automatically.
            </Typography>
            <TextField
              fullWidth
              label="Username or email"
              value={connectUsername}
              onChange={(e) => setConnectUsername(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="password"
              label="Password"
              value={connectPassword}
              onChange={(e) => setConnectPassword(e.target.value)}
            />
          </>
        )}
      </FormDialog>

      {results.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Results</Typography>
            {results.map((r, i) => (
              <Box key={i} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={r.platform} size="small" />
                {r.success ? (
                  <StatusChip label="Success" color="success" variant="filled" />
                ) : (
                  <StatusChip status="failed" label={r.error || 'Failed'} />
                )}
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {history.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: '#f0f6fc', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon sx={{ color: '#58a6ff' }} />
              History
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {history.map((entry) => (
                <Box
                  key={entry.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    py: 1,
                    px: 2,
                    borderRadius: 1,
                    bgcolor: entry.allSuccess ? 'rgba(56,139,253,0.08)' : 'rgba(248,81,73,0.08)',
                    border: '1px solid',
                    borderColor: entry.allSuccess ? 'rgba(56,139,253,0.3)' : 'rgba(248,81,73,0.3)',
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#c9d1d9', minWidth: 140 }}>
                    {new Date(entry.date).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#f0f6fc', flex: 1, minWidth: 120 }}>
                    {entry.title}
                  </Typography>
                  {entry.platforms?.map((r, i) => (
                    <Chip
                      key={i}
                      label={r.platform}
                      size="small"
                      color={r.success ? 'success' : 'error'}
                      variant="outlined"
                    />
                  ))}
                  {entry.error && !entry.platforms?.length && (
                    <Chip label={entry.error} size="small" color="error" variant="outlined" />
                  )}
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
